'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { AVATAR_COLORS, client } from '@/components/site/visuals/demo-data'
import { useScenario } from '@/components/site/visuals/home/use-scenario'
import { useVisualCopy } from '@/components/site/visuals/visual-copy'
import { CheckIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx/lite'

type Step = { title: string; desc: string }

/**
 * The migration-step detail rows. Clients and colleagues come from the shared demo dataset, so the
 * three clients a visitor sees being imported here are the same three they meet on the planning
 * boards further up the page.
 */
const IMPORT_ROWS = [
  { nr: '10010', source: 'import' },
  { nr: '10040', source: 'API' },
  { nr: '10044', source: 'KvK' },
] as const

const RECURRING_ROWS = [
  { nr: '10010', frequency: 'monthly' },
  { nr: '10040', frequency: 'quarterly' },
  { nr: '10044', frequency: 'monthly' },
] as const satisfies readonly { nr: string; frequency: 'monthly' | 'quarterly' }[]

const INVITED_TEAM = ['MV', 'AS', 'DA'] as const

const ROW_COUNT = 3

// A 12-beat loop (1s/beat): step 1 active+revealing rows (0-2), step 2 active+revealing (3-6),
// step 3 active+revealing chips then the label (7-9), then a 2-beat rest with everything shown
// before the loop resets.
const BEAT_MS = 1000
const ACTIVE_BEATS = 10
const REST_BEATS = 2
const TOTAL_BEATS = ACTIVE_BEATS + REST_BEATS
const LABEL_BEAT = 9

function isActiveStep(index: number, beat: number): boolean {
  if (beat >= ACTIVE_BEATS) return false // resting: no card highlighted
  if (index === 0) return beat <= 2
  if (index === 1) return beat >= 3 && beat <= 6
  return beat >= 7
}

/**
 * Whether a step has actually finished — the header check only appears at that moment, so the
 * checks tick off one by one as the walkthrough progresses instead of all three sitting there from
 * the start. `beat === null` (SSR / no-JS / reduced motion) is the resolved end-state: all done.
 */
function isStepDone(index: number, beat: number | null): boolean {
  if (beat === null) return true
  if (beat >= ACTIVE_BEATS) return true // resting: the walkthrough completed
  if (index === 0) return beat >= 2
  if (index === 1) return beat >= 6
  return beat >= LABEL_BEAT
}

function stepRevealCount(index: number, beat: number | null): number {
  if (beat === null) return ROW_COUNT // SSR / reduced-motion / not-yet-animating: full end-state
  if (index === 0) return Math.min(beat + 1, ROW_COUNT)
  if (index === 1) return Math.min(Math.max(beat - 3, 0), ROW_COUNT)
  // index === 2: avatar chips pop in over beats 7 and 8
  if (beat < 7) return 0
  if (beat === 7) return 1
  return ROW_COUNT
}

/**
 * Renders the 3 migration step cards, driven by `useScenario`'s shared beat counter: a highlight
 * ring cycles across the cards while each card's rows/checks/chips reveal in sequence, and each
 * step's header check only appears once that step is finished.
 *
 * `beat === null` (SSR, pre-hydration, reduced motion) resolves to the full end-state — every row
 * revealed, all three steps checked, no ring.
 */
export function MigrationStepsGrid({ steps }: { steps: Step[] }) {
  const { ref, beat } = useScenario(TOTAL_BEATS, { intervalMs: BEAT_MS })

  return (
    <div ref={ref} className="grid grid-cols-1 gap-2 lg:grid-cols-3">
      {steps.map((step, index) => (
        <StepCard
          key={step.title}
          index={index}
          title={step.title}
          desc={step.desc}
          active={beat !== null && isActiveStep(index, beat)}
          done={isStepDone(index, beat)}
          revealCount={stepRevealCount(index, beat)}
          labelVisible={beat === null || beat >= LABEL_BEAT}
        />
      ))}
    </div>
  )
}

function StepCard({
  index,
  title,
  desc,
  active,
  done,
  revealCount,
  labelVisible,
}: {
  index: number
  title: string
  desc: string
  active: boolean
  done: boolean
  revealCount: number
  labelVisible: boolean
}) {
  const { t } = useVisualCopy()

  return (
    // Only the step currently being walked through is outlined, in the same soft accent the
    // testimonial slider uses while it holds the spotlight (see proof-stats-client.tsx). Ringing
    // the resting steps too was tried and dropped: with all three outlined, nothing pointed at the
    // step that was actually moving.
    <div
      className={clsx(
        'flex flex-col gap-4 rounded-lg bg-mist-950/2.5 p-6 ring-[1.5px] transition-colors duration-500 ease-out ring-inset dark:bg-white/5',
        active ? 'ring-accent/25 dark:ring-accent-bright/30' : 'ring-transparent',
      )}
    >
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm/7 font-semibold text-mist-600 dark:text-mist-400">
          {t.word.step} {index + 1}
        </span>
        <CheckIcon
          aria-hidden="true"
          className={clsx(
            'size-4 shrink-0 stroke-2 text-accent transition-all duration-300 ease-out dark:text-accent-bright',
            done ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
          )}
        />
      </div>
      <div className="flex flex-col gap-1">
        <h3 className="text-base/7 font-semibold text-mist-950 dark:text-white">{title}</h3>
        <p className="text-sm/7 text-mist-700 dark:text-mist-400">{desc}</p>
      </div>
      <hr className="border-t border-mist-950/10 dark:border-white/10" />
      <StepDetail index={index} revealCount={revealCount} labelVisible={labelVisible} />
    </div>
  )
}

function StepDetail({
  index,
  revealCount,
  labelVisible,
}: {
  index: number
  revealCount: number
  labelVisible: boolean
}) {
  const { t } = useVisualCopy()

  if (index === 0) {
    return (
      <ul className="flex flex-col gap-3">
        {IMPORT_ROWS.map((row, rowIndex) => (
          <li
            key={row.nr}
            className={clsx(
              'flex items-center gap-3 text-sm/7 transition-all duration-300 ease-out',
              rowIndex < revealCount ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0',
            )}
          >
            <CheckBadge />
            <span className="min-w-0 flex-1 truncate text-mist-950 dark:text-white">{client(row.nr).name}</span>
            <span className="shrink-0 text-mist-600 dark:text-mist-400">{row.source}</span>
          </li>
        ))}
      </ul>
    )
  }

  if (index === 1) {
    return (
      <ul className="flex flex-col gap-3">
        {RECURRING_ROWS.map((row, rowIndex) => (
          <li
            key={row.nr}
            className={clsx(
              'flex items-center gap-3 text-sm/7 transition-all duration-300 ease-out',
              rowIndex < revealCount ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0',
            )}
          >
            <CheckBadge />
            <span className="min-w-0 flex-1 truncate text-mist-950 dark:text-white">{client(row.nr).short}</span>
            <span className="shrink-0 text-mist-600 dark:text-mist-400">{t.screen.migration[row.frequency]}</span>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div className="flex items-center gap-4">
      <div aria-hidden="true" className="flex -space-x-2">
        {INVITED_TEAM.map((initials, memberIndex) => (
          <span
            key={initials}
            className={clsx(
              'flex size-[22px] items-center justify-center rounded-full text-[10px] font-bold text-white ring-2 ring-mist-100 transition-all duration-300 ease-out dark:ring-mist-950',
              memberIndex < revealCount ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
            )}
            style={{ backgroundColor: AVATAR_COLORS[initials] }}
          >
            {initials}
          </span>
        ))}
      </div>
      <span
        className={clsx(
          'text-sm/7 text-mist-700 transition-opacity duration-300 dark:text-mist-400',
          labelVisible ? 'opacity-100' : 'opacity-0',
        )}
      >
        {t.screen.migration.invitesSent}
      </span>
    </div>
  )
}

function CheckBadge() {
  return (
    <span className="flex size-4 shrink-0 items-center justify-center rounded-xs bg-accent">
      <CheckIcon aria-hidden="true" className="size-2.5 stroke-[3] text-white" />
    </span>
  )
}
